import classNames from 'classnames';
import { useRef, useState, useId, useEffect } from 'react';

import { useOnResize } from 'src/hooks/useOnResize';

import { debounce } from 'src/utils/debounce';
import { hasNonWhiteTheme } from 'src/utils/hasNonWhiteTheme';

import { CoreAccordionData } from 'src/types/components';

import PlusIcon from 'assets/icons/plus.svg';

import RichText from 'components/BaseHelpers/RichText';
import Text from 'components/BaseHelpers/Text';

import styles from './CoreAccordion.module.scss';

export type CoreAccordionProps = CoreAccordionData;

type VisibilityMap = Record<string, boolean>;

// expand all button
// show `expand` if any rows are collapsed
// show 'collapse' if all rows are expanded
// only show if there are 2+ items
type ToggleAllButtonProps = {
  visibility: VisibilityMap;
  setVisibility: (newVisibility: VisibilityMap) => void;
};
const ToggleAllButton = ({ visibility, setVisibility }: ToggleAllButtonProps) => {
  if (Object.values(visibility).length < 2) {
    return null;
  }

  const hasExpandedAll = !Object.values(visibility).includes(false);

  const expandAll = () => {
    setVisibility(mapVisibilityToValue(Object.keys(visibility), true));
  };

  const collapseAll = () => {
    setVisibility(mapVisibilityToValue(Object.keys(visibility), false));
  };

  const buttonCopy = hasExpandedAll ? 'Collapse All' : 'Expand All';
  const buttonOnClick = hasExpandedAll ? collapseAll : expandAll;
  return (
    <button
      onClick={buttonOnClick}
      data-show-all-state={hasExpandedAll ? 'show' : 'hide'}
      className={styles.expandAllButton}
    >
      {buttonCopy} <PlusIcon className={styles.expandAllIcon} />
    </button>
  );
};

type AccordionItemProps = {
  id: string;
  open: boolean;
  title: string;
  content: string;
  toggleVisibility: () => void;
};
const AccordionItem = ({ id, open, title, content, toggleVisibility }: AccordionItemProps) => {
  const { setOnResizeFn } = useOnResize();
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState(0);

  const updateHeight = debounce(() => {
    setHeight(contentRef.current?.getBoundingClientRect().height ?? 0);
  }, 100);

  setOnResizeFn(() => {
    updateHeight();
  });

  useEffect(() => {
    if (contentRef !== null) {
      updateHeight();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentRef]);

  return (
    <div
      className={classNames(styles.item)}
      data-state={open ? 'show' : 'hide'}
      style={{ ['--height']: `${height}px` }}
    >
      <button
        className={styles.trigger}
        aria-controls={id}
        id={`${id}-trigger`}
        aria-expanded={open}
        onClick={toggleVisibility}
      >
        <div className={styles.triggerCopyWrapper}>
          <Text className={styles.triggerCopy} field={title} />
        </div>
        <PlusIcon aria-hidden="true" className={styles.icon} />
      </button>
      <div className={styles.content} role="region" id={id} aria-labelledby={`${id}-trigger`}>
        <RichText className={styles.richText} field={content} ref={contentRef} />
      </div>
    </div>
  );
};

const mapVisibilityToValue = (items: string[], value = false) => {
  return items.reduce((map, item) => {
    return {
      ...map,
      [item]: value,
    };
  }, {}) as VisibilityMap;
};

const CoreAccordion = ({ fields }: CoreAccordionProps): JSX.Element | null => {
  const { header, items, theme, anchorTag } = fields;
  const uniqueID = useId();

  const uniqueNames = items.map((_, index) => `accordion-item-${index}-${uniqueID}`);
  const [visibility, setVisibility] = useState(mapVisibilityToValue(uniqueNames));

  const toggleVisibility = (id: string) => {
    setVisibility({
      ...visibility,
      [id]: !visibility[id],
    });
  };

  const { eyebrow, heading, description } = header;

  const hasDarkerBackground = hasNonWhiteTheme(theme) && 'spacer--with-vertical-padding';

  return (
    <section
      id={anchorTag}
      className={classNames(styles.accordion, styles[theme], 'spacer', hasDarkerBackground)}
    >
      <div className={classNames('container', 'container--true-1440')}>
        <div className={styles.contentWrapper}>
          <div className={styles.headingWrapper}>
            {!!eyebrow && <Text className={styles.eyebrow} tag="p" field={eyebrow} />}
            {!!heading && <Text tag="h3" field={heading} className={styles.heading} />}
            {!!description && <RichText className={styles.description} field={description} />}
            <ToggleAllButton visibility={visibility} setVisibility={setVisibility} />
          </div>
          <div className={styles.accordionWrapper}>
            {items.map((item, index) => {
              const id = uniqueNames[index];
              return (
                <AccordionItem
                  {...item}
                  key={id}
                  open={visibility[id]}
                  id={id}
                  toggleVisibility={() => toggleVisibility(id)}
                />
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CoreAccordion;
